import { createVoteIntent, submitSignedVoteTransaction } from "./api";
import { getPendingAffiliateId } from "./affiliate";
import { getWalletSigningMode, isEmbeddedPresignSubmitMode, isEmbeddedSignOnlyMode } from "./walletSigningReadiness.js";
import {
  assertWalletCanSubmitTransaction,
  deserializeWeb3TransactionBase64 as deserializeTransactionBase64,
  resolveWalletAddress,
  serializeWeb3TransactionBase64 as serializeTransactionBase64,
  signSerializedTransactionBase64
} from "./walletTransactionSigning";

export { deserializeTransactionBase64, serializeTransactionBase64 };

export function recipientFundingSol(intent) {
  const lamports = Number(intent?.recipientFundingTotalLamports || 0);
  if (!Number.isFinite(lamports) || lamports <= 0) return "";
  return (lamports / 1_000_000_000).toFixed(9).replace(/0+$/, "").replace(/\.$/, "");
}

export class VoteConfirmationCancelledError extends Error {
  constructor(message = "Vote approval cancelled.") {
    super(message);
    this.name = "VoteConfirmationCancelledError";
    this.code = "vote_confirmation_cancelled";
  }
}

export async function signAndSubmitVote({ wallet, marketId, optionId, voteAmount, paymentTokenId, onIntent, confirmEmbeddedTransaction }) {
  return signAndSubmitVoteAttempt({
    wallet,
    marketId,
    optionId,
    voteAmount,
    paymentTokenId,
    onIntent,
    confirmEmbeddedTransaction,
    allowStaleRetry: true
  });
}

async function signAndSubmitVoteAttempt({
  wallet,
  marketId,
  optionId,
  voteAmount,
  paymentTokenId,
  onIntent,
  confirmEmbeddedTransaction,
  allowStaleRetry
}) {
  assertWalletCanSubmitTransaction(wallet);
  const intent = await createVoteIntent({
    marketId,
    optionId,
    voteAmount,
    paymentTokenId,
    affiliateId: getPendingAffiliateId()
  });
  onIntent?.(intent);
  if (new Date(intent.expiresAt).getTime() <= Date.now() + 15_000) {
    throw new Error("Vote transaction quote expired before signing. Please try again.");
  }

  const signingMode = getWalletSigningMode(wallet);
  if (isEmbeddedPresignSubmitMode(signingMode)) {
    const expectedUserWallet = resolveWalletAddress(wallet);
    const confirmation = typeof confirmEmbeddedTransaction === "function"
      ? await confirmEmbeddedTransaction({
        kind: "vote",
        marketId,
        optionId,
        voteAmount,
        intent,
        expectedUserWallet
      })
      : null;
    if (confirmation === false) {
      throw new VoteConfirmationCancelledError();
    }
    return wallet.signAndSendTransaction({
      kind: "vote",
      voteIntentId: intent.voteIntentId,
      unsignedTransactionBase64: intent.unsignedTransactionBase64,
      expectedUserWallet,
      voteAmount,
      skipWalletUi: Boolean(confirmation?.skipWalletUi)
    });
  }

  let signOptions;
  if (isEmbeddedSignOnlyMode(signingMode)) {
    if (typeof confirmEmbeddedTransaction !== "function") {
      throw new Error("Embedded transaction confirmation is unavailable.");
    }
    const expectedUserWallet = resolveWalletAddress(wallet);
    const confirmation = await confirmEmbeddedTransaction({
      kind: "vote",
      marketId,
      optionId,
      voteAmount,
      intent,
      expectedUserWallet
    });
    if (confirmation === false) {
      throw new VoteConfirmationCancelledError();
    }
    signOptions = { skipWalletUi: Boolean(confirmation?.skipWalletUi) };
  }

  const { serializedTransactionBase64, walletAddress } = await signSerializedTransactionBase64({
    wallet,
    unsignedTransactionBase64: intent.unsignedTransactionBase64,
    signOptions
  });

  try {
    return await submitSignedVoteTransaction({
      voteIntentId: intent.voteIntentId,
      serializedTransactionBase64,
      expectedUserWallet: walletAddress
    });
  } catch (error) {
    if (allowStaleRetry && /blockhash expired|intent expired/i.test(error?.message || "")) {
      return signAndSubmitVoteAttempt({
        wallet,
        marketId,
        optionId,
        voteAmount,
        paymentTokenId,
        onIntent,
        confirmEmbeddedTransaction,
        allowStaleRetry: false
      });
    }
    throw error;
  }
}
